import { useCallback } from 'react';
import Button from 'src/components/button/button';
import FormInput from 'src/components/form-input';
import FormSwitch from 'src/components/form-switch';
import IPInput from 'src/components/ip-input';
import { IVlanItem } from 'src/context/global/types';
import { useImportConfiguration } from 'src/context/import-configuration';
import { calculateNetworkAddress2 } from 'src/utils/ip';

function VlanRowEditor({
  vlan,
  onChange,
}: {
  vlan: IVlanItem;
  onChange: (next: IVlanItem) => void;
}) {
  return (
    <tr className="border-b border-gray-200">
      <td className="px-1 py-1">
        <FormInput
          id={`color-${vlan.id}`}
          name={`color-${vlan.id}`}
          type="color"
          className="w-10 rounded-none p-0.5"
          value={vlan.color || '#cccccc'}
          onChange={(e) => onChange({ ...vlan, color: e.target.value })}
        />
      </td>
      <td className="px-2 py-1 align-middle">
        <span className="font-mono text-sm">{vlan.vlanId}</span>
      </td>
      <td className="px-1 py-1">
        <FormInput
          type="text"
          name={`description-${vlan.id}`}
          value={vlan.description}
          onChange={(e) => onChange({ ...vlan, description: e.target.value })}
          id={`description-${vlan.id}`}
        />
      </td>
      <td className="px-1 py-1">
        <div className="flex w-full flex-row items-start justify-start gap-2">
          <IPInput
            id={`ip-${vlan.id}`}
            name={`ip-${vlan.id}`}
            wrapperClassName="w-auto"
            placeholder="192.168.1.0"
            value={vlan.ip || ''}
            // className={twMerge([errorMessage ? 'border-red-500' : ''])}
            onChange={(value) => {
              onChange({ ...vlan, ip: value });
            }}
            dataTestId={`ip-input-${vlan.id}`}
          />
          <span className="mt-2 text-gray-500">/</span>
          <IPInput
            id={`subnet-mask-${vlan.id}`}
            name={`subnet-mask-${vlan.id}`}
            wrapperClassName="w-auto"
            placeholder="255.255.255.0"
            value={vlan.subnetMask || ''}
            onChange={(value) => {
              onChange({ ...vlan, subnetMask: value });
            }}
            dataTestId={`subnet-mask-input-${vlan.id}`}
          />
        </div>
      </td>
    </tr>
  );
}

export default function ImportStepVlanSubnet() {
  const {
    dialogState,
    updateProjectJsonData,
    importReview,
    setVlanSubnetConfirmed,
  } = useImportConfiguration();

  const invalidateConfirm = useCallback(() => {
    setVlanSubnetConfirmed(false);
  }, [setVlanSubnetConfirmed]);

  const patchVlan = useCallback(
    (index: number, next: IVlanItem) => {
      updateProjectJsonData((prev) => {
        const vlans = [...prev.vlans];
        const ipSubnet = calculateNetworkAddress2(
          next.ip || '',
          next.subnetMask || '',
        );
        vlans[index] = { ...next, ipSubnet };
        return { ...prev, vlans };
      });
      invalidateConfirm();
    },
    [updateProjectJsonData, invalidateConfirm],
  );

  const patchGlobalSubnet = useCallback(
    (patch: { ip?: string; ipSubnet?: string; subnetMask?: string }) => {
      updateProjectJsonData((prev) => {
        const ip = patch.ip ?? prev.globalIpSubnet?.ip;
        const subnetMask = patch.subnetMask ?? prev.globalIpSubnet?.subnetMask;
        const ipSubnet = calculateNetworkAddress2(ip || '', subnetMask || '');
        return {
          ...prev,
          globalIpSubnet: {
            ...prev.globalIpSubnet,
            ...patch,
            ipSubnet,
          },
        };
      });
      invalidateConfirm();
    },
    [updateProjectJsonData, invalidateConfirm],
  );

  if (!dialogState?.projectJsonData) return null;

  const { vlans, globalIpSubnet, useCustomVlan } = dialogState.projectJsonData;

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600">
          Switch between VLAN list and network IP subnet. Confirm when done
          before the next step.
        </p>
        <Button
          type="button"
          color={importReview.vlanSubnetConfirmed ? 'green' : 'blue'}
          onClick={() => setVlanSubnetConfirmed(true)}
        >
          {importReview.vlanSubnetConfirmed ? 'Confirmed ✓' : 'Confirm step'}
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-2">
        <h3 className="text-lg font-semibold text-gray-800">
          {useCustomVlan ? 'VLANs' : 'Network IP Subnet'}
        </h3>
        <div className="flex flex-row items-center justify-start gap-2">
          <FormSwitch
            id="use-custom-vlan"
            name="use-custom-vlan"
            data-testid="dialog-global-vlan-use-custom-vlan-switch"
            wrapperClassName="w-auto items-start justify-start gap-2"
            checked={useCustomVlan}
            onChange={(value) => {
              updateProjectJsonData((prev) => ({
                ...prev,
                useCustomVlan: value,
              }));
              invalidateConfirm();
            }}
          />
          <span className="text-sm text-gray-500">Use VLANs</span>
        </div>
      </div>

      <div className="grid gap-6">
        {useCustomVlan && (
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="px-2 py-1">Color</th>
                <th className="px-2 py-1">VLAN ID</th>
                <th className="px-2 py-1">Description</th>
                <th className="px-2 py-1">IP Subnet</th>
              </tr>
            </thead>
            <tbody>
              {vlans.map((vlan, index) => (
                <VlanRowEditor
                  key={vlan.id}
                  vlan={vlan}
                  onChange={(next) => patchVlan(index, next)}
                />
              ))}
            </tbody>
          </table>
        )}

        {!useCustomVlan && (
          <div className="flex w-full flex-row items-start justify-start gap-2">
            <IPInput
              id="global-ip"
              name="globalIp"
              wrapperClassName="w-auto"
              placeholder="192.168.1.0"
              value={globalIpSubnet?.ip || ''}
              // className={twMerge([errorMessage ? 'border-red-500' : ''])}
              onChange={(value) => {
                patchGlobalSubnet({ ip: value });
              }}
              dataTestId="global-ip-subnet-ip-input"
            />
            <span className="mt-2 text-gray-500">/</span>
            <IPInput
              id="global-netmask"
              name="globalNetmask"
              wrapperClassName="w-auto"
              placeholder="255.255.255.0"
              value={globalIpSubnet?.subnetMask || ''}
              onChange={(value) => {
                patchGlobalSubnet({ subnetMask: value });
              }}
              dataTestId="global-ip-subnet-subnet-mask-input"
            />
          </div>
        )}
      </div>
    </div>
  );
}
